/**
 * Firestore Queries for Email Accounts
 *
 * Client-side Firestore operations for managing OAuth2 email accounts.
 * Uses real-time listeners for automatic UI updates.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  Timestamp,
  updateDoc,
  writeBatch,
  where,
  QueryConstraint,
  Unsubscribe
} from "firebase/firestore";
import { firestore } from "@/base";
import type { EmailAccount, EmailAccountFormData } from "./schema/email-account.types";

/**
 * Collection reference
 */
const EMAIL_ACCOUNTS_COLLECTION = "email_accounts";

/**
 * Get email accounts collection reference
 */
function getEmailAccountsCollection() {
  return collection(firestore, EMAIL_ACCOUNTS_COLLECTION);
}

/**
 * List all email accounts for a licenciada
 * @param licenciadaId - Licenciada ID
 * @param onlyActive - Filter only active accounts (default: true)
 * @returns Promise of EmailAccount array
 */
export async function listEmailAccounts(licenciadaId: string, onlyActive: boolean = true): Promise<EmailAccount[]> {
  const constraints: QueryConstraint[] = [where("licenciada_id", "==", licenciadaId)];

  if (onlyActive) {
    constraints.push(where("is_active", "==", true));
  }

  const q = query(getEmailAccountsCollection(), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((document_) => ({
    id: document_.id,
    ...document_.data()
  })) as EmailAccount[];
}

/**
 * Get a specific email account by ID
 * @param accountId - Account document ID
 * @returns Promise of EmailAccount or null
 */
export async function getEmailAccount(accountId: string): Promise<EmailAccount | null> {
  const documentReference = doc(firestore, EMAIL_ACCOUNTS_COLLECTION, accountId);
  const snapshot = await getDoc(documentReference);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data()
  } as EmailAccount;
}

/**
 * Get the default email account for a licenciada
 * @param licenciadaId - Licenciada ID
 * @returns Promise of EmailAccount or null
 */
export async function getDefaultEmailAccount(licenciadaId: string): Promise<EmailAccount | null> {
  const q = query(
    getEmailAccountsCollection(),
    where("licenciada_id", "==", licenciadaId),
    where("is_default", "==", true),
    where("is_active", "==", true)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const firstDocument = snapshot.docs[0];
  if (!firstDocument) {
    return null;
  }

  return {
    id: firstDocument.id,
    ...firstDocument.data()
  } as EmailAccount;
}

/**
 * Create a new email account
 *
 * Creates a new email account via Cloud Function, which handles
 * server-side encryption of the client_secret.
 * @param licenciadaId - Licenciada ID
 * @param data - Email account form data (including client_secret)
 * @returns Promise of created account ID
 */
export async function createEmailAccount(licenciadaId: string, data: EmailAccountFormData): Promise<string> {
  // Call Cloud Function to create account with encrypted credentials
  const { createEmailAccountViaCloudFunction } = await import("./functions");
  return await createEmailAccountViaCloudFunction(licenciadaId, data);
}

/**
 * Set an email account as the default for a licenciada
 * @param accountId - Account document ID
 * @returns Promise<void>
 */
export async function setDefaultEmailAccount(accountId: string): Promise<void> {
  const documentReference = doc(firestore, EMAIL_ACCOUNTS_COLLECTION, accountId);
  const accountSnapshot = await getDoc(documentReference);

  if (!accountSnapshot.exists()) {
    throw new Error(`Email account not found: ${accountId}`);
  }

  const accountData = accountSnapshot.data() as EmailAccount;
  const licenciadaId = accountData.licenciada_id;

  if (!licenciadaId) {
    throw new Error(`Email account missing licenciada_id: ${accountId}`);
  }

  const defaultsQuery = query(
    getEmailAccountsCollection(),
    where("licenciada_id", "==", licenciadaId),
    where("is_default", "==", true)
  );

  const defaultsSnapshot = await getDocs(defaultsQuery);
  const batch = writeBatch(firestore);
  const now = Timestamp.now();

  for (const defaultDocument of defaultsSnapshot.docs) {
    if (defaultDocument.id === accountId) {
      continue;
    }

    batch.update(defaultDocument.ref, {
      is_default: false,
      updated_at: now
    });
  }

  batch.update(documentReference, {
    is_default: true,
    updated_at: now
  });

  await batch.commit();
}

/**
 * Delete an email account (soft delete - marks as inactive)
 * @param accountId - Account document ID
 * @returns Promise<void>
 */
export async function deleteEmailAccount(accountId: string): Promise<void> {
  const documentReference = doc(firestore, EMAIL_ACCOUNTS_COLLECTION, accountId);

  await updateDoc(documentReference, {
    is_active: false,
    is_default: false,
    updated_at: Timestamp.now()
  });
}

/**
 * Subscribe to real-time updates for email accounts
 * @param licenciadaId - Licenciada ID
 * @param onlyActive - Filter only active accounts (default: true)
 * @param callback - Callback function called when data changes
 * @returns Unsubscribe function
 * @example
 * const unsubscribe = subscribeToEmailAccounts(
 *   "licenciada123",
 *   true,
 *   (accounts) => {
 *     console.log("Accounts updated:", accounts);
 *   }
 * );
 *
 * // Later, to stop listening:
 * unsubscribe();
 */
export function subscribeToEmailAccounts(
  licenciadaId: string,
  onlyActive: boolean = true,
  callback: (accounts: EmailAccount[]) => void
): Unsubscribe {
  const constraints: QueryConstraint[] = [where("licenciada_id", "==", licenciadaId)];

  if (onlyActive) {
    constraints.push(where("is_active", "==", true));
  }

  const q = query(getEmailAccountsCollection(), ...constraints);

  return onSnapshot(
    q,
    (snapshot) => {
      const accounts = snapshot.docs.map((document_) => ({
        id: document_.id,
        ...document_.data()
      })) as EmailAccount[];

      callback(accounts);
    },
    (error) => {
      console.error("Error in email accounts subscription:", error);
      callback([]);
    }
  );
}

/**
 * Subscribe to a specific email account updates
 * @param accountId - Account document ID
 * @param callback - Callback function called when account changes
 * @returns Unsubscribe function
 */
export function subscribeToEmailAccount(
  accountId: string,
  callback: (account: EmailAccount | null) => void
): Unsubscribe {
  const documentReference = doc(firestore, EMAIL_ACCOUNTS_COLLECTION, accountId);

  return onSnapshot(
    documentReference,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }

      const account = {
        id: snapshot.id,
        ...snapshot.data()
      } as EmailAccount;

      callback(account);
    },
    (error) => {
      console.error("Error in email account subscription:", error);
      callback(null);
    }
  );
}

/**
 * Update email account status
 * (Typically updated by backend, but can be useful for UI optimistic updates)
 * @param accountId - Account document ID
 * @param status - New status
 * @returns Promise<void>
 */
export async function updateEmailAccountStatus(accountId: string, status: EmailAccount["status"]): Promise<void> {
  const documentReference = doc(firestore, EMAIL_ACCOUNTS_COLLECTION, accountId);

  await updateDoc(documentReference, {
    status,
    updated_at: Timestamp.now()
  });
}
